/**
 * When the backend fails to resolve a teamSlug performer it sometimes returns
 * the entire league/category inventory (1k+ events). Prefer empty/filtered
 * results over showing unrelated tickets on a team page.
 */

const CATEGORY_LEAK_TOTAL_THRESHOLD = 100;
// A real category leak returns mostly unrelated events; a legitimately large
// team inventory (e.g. a full season of listings) has nearly all of its page
// events match. Only treat a large total as a leak when the page itself
// backs that up, so we don't clobber a valid total_entries (and kill
// pagination) for teams with more than CATEGORY_LEAK_TOTAL_THRESHOLD events.
const CATEGORY_LEAK_MATCH_RATIO_THRESHOLD = 0.5;

const STOP_WORDS = new Set([
  'state',
  'university',
  'college',
  'tech',
  'the',
  'and',
  'at',
  'of',
  'san',
  'st',
  'football',
  'basketball',
  'baseball',
  'hockey',
  'soccer',
  'season',
  'tickets',
  'includes',
  'regular',
  'home',
  'games',
  'vs',
  'versus',
  'mens',
  'womens',
]);

const distinctiveTokens = (teamSlug: string) =>
  teamSlug
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length > 2 && !STOP_WORDS.has(token));

export const eventNameMatchesTeamSlug = (
  eventName = '',
  teamSlug = ''
): boolean => {
  if (!eventName || !teamSlug) return false;

  const normalizedEvent = eventName
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ');

  const tokens = distinctiveTokens(teamSlug);
  if (tokens.length === 0) return false;

  const hits = tokens.filter((token) => normalizedEvent.includes(token));
  if (tokens.length <= 2) return hits.length >= 1;
  return hits.length >= Math.min(2, Math.ceil(tokens.length * 0.4));
};

export const eventInvolvesTeam = (
  event: {
    name?: string;
    performances?: Array<{
      performer?: { id?: number; name?: string; slug?: string };
    }>;
  },
  teamSlug = '',
  performerId?: number | null
): boolean => {
  if (performerId != null) {
    const byId = event.performances?.some(
      (performance) => performance?.performer?.id === performerId
    );
    if (byId) return true;
  }

  if (eventNameMatchesTeamSlug(event.name, teamSlug)) return true;

  const slug = teamSlug.toLowerCase();
  return Boolean(
    event.performances?.some((performance) => {
      const performerSlug = performance?.performer?.slug?.toLowerCase() || '';
      const performerName = performance?.performer?.name || '';
      return (
        (performerSlug &&
          (performerSlug === slug ||
            performerSlug.startsWith(`${slug}-`) ||
            performerSlug.includes(slug))) ||
        eventNameMatchesTeamSlug(performerName, teamSlug)
      );
    })
  );
};

type CategoryEventsResponse = {
  events?: Array<{
    name?: string;
    performances?: Array<{
      performer?: { id?: number; name?: string; slug?: string };
    }>;
  }>;
  total_entries?: number;
  [key: string]: unknown;
};

/**
 * Drop category-leak payloads for team pages. If the response is clearly the
 * full league inventory, keep only events that involve the requested team; if
 * none on this page match, return an empty list instead of unrelated tickets.
 */
export const guardCategoryEventsForTeam = <T extends CategoryEventsResponse>(
  response: T,
  teamSlug?: string,
  performerId?: number | null
): T => {
  if (!teamSlug || !response) return response;

  const events = response.events ?? [];
  const totalEntries = response.total_entries ?? events.length;

  if (totalEntries <= CATEGORY_LEAK_TOTAL_THRESHOLD) return response;

  const filtered = events.filter((event) =>
    eventInvolvesTeam(event, teamSlug, performerId)
  );
  const matchRatio = events.length ? filtered.length / events.length : 0;
  const looksLikeCategoryLeak =
    matchRatio <= CATEGORY_LEAK_MATCH_RATIO_THRESHOLD;

  if (!looksLikeCategoryLeak) return response;

  return {
    ...response,
    events: filtered,
    total_entries: filtered.length,
  };
};

type LocationEventsResponse = {
  events?: Array<{
    name?: string;
    performances?: Array<{
      performer?: { id?: number; name?: string; slug?: string };
    }>;
  }>;
  totalEvents?: number;
  total_entries?: number;
  [key: string]: unknown;
};

export const guardLocationEventsForTeam = <T extends LocationEventsResponse>(
  response: T,
  teamSlug?: string,
  performerId?: number | null
): T => {
  if (!teamSlug || !response) return response;

  const events = response.events ?? [];
  const total = response.totalEvents ?? response.total_entries ?? events.length;

  if (total <= CATEGORY_LEAK_TOTAL_THRESHOLD) return response;

  const filtered = events.filter((event) =>
    eventInvolvesTeam(event, teamSlug, performerId)
  );
  const matchRatio = events.length ? filtered.length / events.length : 0;
  const looksLikeCategoryLeak =
    matchRatio <= CATEGORY_LEAK_MATCH_RATIO_THRESHOLD;

  if (!looksLikeCategoryLeak) return response;

  return {
    ...response,
    events: filtered,
    totalEvents: filtered.length,
    total_entries: filtered.length,
  };
};
